// ==========================================
// 6. src/components/sections/GuideSection.js
// ==========================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users, Shield, Clock } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import SectionHeader from '../common/SectionHeader';

const GuidePoint = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
      <Icon className="w-6 h-6 text-blue-600" />
    </div>
    <div>
      <h3 className="font-semibold text-lg text-gray-900 mb-1">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  </div>
);

// ---- Rotating Testimonials ----
const RotatingTestimonials = () => {
  const items = useMemo(
    () => [
      {
        quote: `
          SYBR turned my nightmare days into seamless operations. 
          The difference started immediately—their system is refreshingly easy to use (no steep learning curve, no frustration), 
          and their customer support is genuinely exceptional. Whether I need strategic guidance or quick troubleshooting, they're there. 
          It's the reliability I desperately needed but couldn't find anywhere else."
        `,
        name: 'Lisa M.',
        title: 'VP, McAbee Trucking INC',
      },
      {
        quote: ` 
          Prior to Sage's switch to SYBR more than half of my day was spent manually tracking loads across multiple platforms. 
          Now, I only have to spend a half hour on it, clearing my schedule dramatically. 
          SYBR's customer support team is absolutely wonderful.
          Highly recommend this platform!
        `,
        name: 'Sarah P.',
        title: 'Government Services, Sage Freight LLC',
      },
      {
        quote: `
          Over the past 8 years, I've worked with several TMS platforms. SYBR TMS is by far the easiest to use. 
          They've built an interface that anyone can navigate, combining powerful data with a clean, minimalist design to create something truly remarkable.
        `,
        name: 'Conner M.',
        title: 'VP Operations, Mail Management Services INC',
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  // Autoplay with pause-on-hover
  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 15000);
    return () => clearInterval(intervalRef.current);
  }, [paused, items.length]);

  const goTo = (i) => setIndex(i % items.length);

  return (
    <div
      className="bg-white rounded-2xl p-8 shadow-xl relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Customer testimonials"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
        >
          <blockquote className="text-lg text-gray-700 italic mb-4">
            “{items[index].quote}”
          </blockquote>
          <div className="flex items-center gap-3">
            {/* <div className="w-12 h-12 bg-gray-200 rounded-full" aria-hidden="true" /> */}
            <div>
              <p className="font-semibold text-gray-900">{items[index].name}</p>
              <p className="text-sm text-gray-500">{items[index].title}</p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Dots */}
      <div className="mt-6 flex items-center gap-2" role="tablist" aria-label="Testimonials navigation">
        {items.map((_, i) => {
          const active = i === index;
          return (
            <button
              key={i}
              onClick={() => goTo(i)}
              role="tab"
              aria-selected={active}
              aria-controls={`testimonial-panel-${i}`}
              className={`h-2.5 w-2.5 rounded-full transition-all ${active ? 'w-6 bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              title={`Show testimonial ${i + 1}`}
            />
          );
        })}
      </div>

      {/* Prev/Next (optional; hidden on small screens) */}
      <div className="hidden sm:flex absolute inset-y-0 justify-between items-center px-2 pointer-events-none">
        {/* <button
          onClick={() => goTo((index - 1 + items.length) % items.length)}
          className="pointer-events-auto p-2 rounded-full bg-white/80 shadow hover:bg-white"
          aria-label="Previous testimonial"
        >
          ‹
        </button>
        <button
          onClick={() => goTo(index + 1)}
          className="pointer-events-auto p-2 rounded-full bg-white/80 shadow hover:bg-white"
          aria-label="Next testimonial"
        >
          ›
        </button> */}
      </div>
    </div>
  );
};

const GuideSection = () => {
  const guidePoints = [
    {
      icon: Users,
      title: 'Operator-First Design',
      description:
        "Every feature solves a real problem we've faced ourselves. No bloat, no complexity for complexity's sake.",
    },
    {
      icon: Shield,
      title: 'USPS-Proven at Scale',
      description:
        'Battle-tested with demanding logistics operations. If it works for USPS, it can work for you.',
    },
    {
      icon: Clock,
      title: 'Always-On Support',
      description: "24/7 dispatch desk and technical support. We're here when you need us, not just 9-5.",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          title="Built by Logistics Pros Who Get It"
          subtitle="We've been in your shoes. SYBR TMS was built from real logistics experience and frustration with 'solutions' that create more problems."
        />

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            {guidePoints.map((point, index) => (
              <GuidePoint key={index} {...point} />
            ))}
          </div>
          <RotatingTestimonials />
        </div>
      </div>
    </section>
  );
};

export default GuideSection;
